import axios from 'axios';
import { useQuery, useQueryClient } from 'react-query';

const apiUrl = (): string => process.env.REACT_APP_BASIX_API || '';

export interface ReminderItemState {
  targetId: string;
  step: number;
  sentAt: string;
}

export interface CollectionsRemindersState {
  config: {
    enabled: boolean;
    enabledAt?: string | null;
    ladderDays: number[];
    repeatEveryDays: number;
  };
  items: ReminderItemState[];
  customers: { contactId: string; sentAt: string }[];
  mutedInvoices: string[];
  mutedCharges: string[];
  optedOutCustomers: string[];
}

// item 1163e — the NEXT ladder rung (days past due) the engine would act on for an item
// currently daysPastDue along: the first ladder day not yet reached, then every
// repeatEveryDays beyond the ladder end. Mirrors CollectionsSettings.CurrentRung server-side.
export const nextRungDays = (
  config: CollectionsRemindersState['config'] | undefined, daysPastDue: number,
): number | null => {
  const ladder = config?.ladderDays ?? [];
  if (ladder.length === 0) return null;
  const upcoming = ladder.find((d) => d > daysPastDue);
  if (upcoming !== undefined) return upcoming;
  const last = ladder[ladder.length - 1];
  const rep = (config?.repeatEveryDays ?? 0) > 0 ? config!.repeatEveryDays : 30;
  const extra = Math.floor((Math.max(daysPastDue, last) - last) / rep) + 1;
  return last + extra * rep;
};

// item 1163e (Chad) — the reminder state the Collections card overlays on its rows: which
// items have been auto-reminded (rung + when), what's muted, which customers opted out,
// plus the mute / opt-out / send-now actions.
const useCollectionsReminders = () => {
  const queryClient = useQueryClient();
  const { data, status } = useQuery('collections-reminders', async () => {
    const r = await axios.get(`${apiUrl()}collections/reminders`);
    return r.data as CollectionsRemindersState;
  }, { staleTime: 60_000, retry: false });

  const refresh = () => queryClient.invalidateQueries('collections-reminders');

  const mute = async (targetId: string, isCharge: boolean, muted: boolean) => {
    await axios.post(`${apiUrl()}collections/mute`, { targetId, isCharge, muted });
    refresh();
  };

  const optOut = async (contactId: string, optOutFlag: boolean) => {
    await axios.post(`${apiUrl()}collections/customer-optout`, { contactId, optOut: optOutFlag });
    refresh();
  };

  const sendNow = async (contactId: string) => {
    await axios.post(`${apiUrl()}collections/send-now/${contactId}`);
    refresh();
  };

  return {
    reminders: data, status, mute, optOut, sendNow,
  };
};

export default useCollectionsReminders;
